// API Types

export interface GeoPoint {
  lat: number
  lon: number
}

export interface ListingImage {
  url: string
  alt_text?: string
  title?: string
}

export interface PriceBreakdown {
  deposit_months?: number
  deposit_amount?: number
  utilities_monthly?: number
  parking_monthly?: number
  internet_monthly?: number
  service_charges_monthly?: number
  price_notes?: string
}

export interface Listing {
  id: string
  is_active?: boolean | number
  title: string
  description?: string
  price: number
  price_currency: string
  rooms?: number
  area?: number
  floor?: number
  total_floors?: number
  city?: string
  district?: string
  street?: string
  location?: GeoPoint
  
  // Features: Dynamic keywords from keywords_cz database
  // All features stored as keywords array (balcony, dishwasher, furnished, pets_allowed, parking, etc.)
  keywords: string[]
  
  // Energy
  energy_class?: string
  
  // Price Analysis
  price_breakdown?: PriceBreakdown

  // Live updates posted by the owner (rendered on cards when present)
  updates?: { id: string; text: string; when: string }[]

  // Images & Meta
  images: ListingImage[]
  image_tags: string[]
  original_url: string
  marketplace?: string
  scraped_at?: string
  relevance_score: number
}

export type SortOption = 
  | 'relevance'
  | 'price_asc'
  | 'price_desc'
  | 'newest'
  | 'oldest'
  | 'area_asc'
  | 'area_desc'
  | 'rooms_asc'
  | 'rooms_desc'
  | 'distance'

export type PropertyType = 'apartment' | 'room' | 'house'
export type TransactionType = 'rent' | 'sale'


export type EnergyClass = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

export interface TravelTimeFilter {
  origin: string
  minutes: number
  mode: 'driving' | 'walking'
}

export interface RentalFilters {
  // Numeric ranges
  rooms_min?: number
  rooms_max?: number
  rooms_exact?: number
  price_max?: number
  price_min?: number
  area_min?: number
  area_max?: number
  floor_min?: number
  floor_max?: number
  
  // Price mode
  use_total_price?: boolean
  
  // Energy
  energy_class?: EnergyClass
  
  // Categorical
  type?: PropertyType  // Property type: apartment, house, room
  transaction_type?: TransactionType

  // Location
  country?: string
  city?: string
  district?: string
  districts?: string[]
}

export interface Location {
  name: string
  district: string
  type: string
  full_name?: string
}

export interface SearchRequest {
  query?: string
  filters?: RentalFilters
  tags?: string[]
  travel_time?: TravelTimeFilter
  sort: SortOption
  limit: number
  offset: number
}

export interface ReferenceLocation {
  lat: number
  lon: number
  label?: string
}

export interface SearchResponse {
  results: Listing[]
  total_count: number
  query?: string
  parsed_filters?: Record<string, any>
  query_filters?: Record<string, any>
  parsed_keywords?: string[]
  semantic_query?: string
  travel_time?: TravelTimeFilter
  reference_location?: ReferenceLocation
  sort: SortOption
  limit: number
  offset: number
}

export interface CountRequest {
  filters?: RentalFilters
  tags?: string[]
  travel_time?: TravelTimeFilter
}

export interface CountResponse {
  total_count: number
}

/** One map pin. Deliberately skinny — the map holds every match at once. */
export interface MapPoint {
  id: string
  lat: number
  lon: number
  price?: number | null
  price_currency?: string | null
  district?: string | null
  /** Carried so the map's price/area/rooms lenses label pins without hydrating. */
  area?: number | null
  rooms?: number | null
}

export interface MapPointsRequest {
  query?: string
  filters?: RentalFilters
  tags?: string[]
  travel_time?: TravelTimeFilter
}

export interface MapPointsResponse {
  points: MapPoint[]
  /** Matches including any without coordinates (so not always points.length). */
  total: number
  truncated: boolean
}

// UI Types

export interface FilterTag {
  id: string
  label: string
  category: 'rooms' | 'price' | 'area' | 'features' | 'amenities' | 'heating' | 'heating_climate' | 'bathroom' | 'kitchen' | 'furniture' | 'furnishing' | 'electronics' | 'internet' | 'connectivity' | 'outdoor' | 'building' | 'parking' | 'parking_storage' | 'contract' | 'lifestyle'
  value: {
    field: keyof RentalFilters
    value: any
  }
}

export type Country = 'CZ' | 'DE' | 'PL'

export interface CountryOption {
  code: Country
  name: string
  enabled: boolean
}

export type Theme = 'light' | 'dark' | 'hacker' | 'system'
export type UiLanguage = 'cs' | 'en'

export type ViewMode = 'list' | 'map'

// User Types

export interface User {
  uid: string
  email?: string
  displayName?: string
  photoURL?: string
}

// Saved Search Types (stored in backend Elasticsearch)

export interface SavedSearchCreate {
  name: string
  query?: string
  filters?: RentalFilters
  tags?: string[]
  travel_time?: TravelTimeFilter
  sort?: SortOption
  is_active?: boolean
  roostr_name?: string
  cadence?: NotificationCadence
}

export type NotificationCadence = 'instant' | 'daily' | 'weekly' | 'off'

export interface SavedSearch {
  id: string
  user_id: string
  name: string
  original_query?: string
  parsed_filters: Record<string, any>
  keywords: string[]
  semantic_query?: string
  has_semantic_search: boolean
  travel_time?: TravelTimeFilter
  sort: SortOption
  tags: string[]
  created_at: string
  is_active: boolean
  cadence: NotificationCadence
  roostr_name?: string
  notification_count?: number
  last_checked_at?: string
  last_executed_at?: string
  new_listings_count?: number
  activity_blocks?: number[]  // 4 values: listings found in each 6-hour block (last 24h)
}

export interface SavedListing {
  id: string          // listing ID
  savedAt: string     // ISO timestamp
}

export type CardDensity = 'comfortable' | 'compact'

export type NotificationFrequency = 'instant' | 'daily' | 'weekly'

// Languages an AI summary can be written in. Unlike the (removed) translation
// feature, Czech IS a valid target — a Czech summary of a Czech listing.
export type SummaryLanguage = 'cs' | 'en' | 'de' | 'uk' | 'ru' | 'es' | 'fr' | 'pl' | 'sk' | 'vi' | 'zh'

export type ListingFreshness = 7 | 14 | 30 | null  // days, null = any age

export interface DealbreakersConfig {
  mustHave: string[]    // keyword IDs that must be present (e.g., 'pets_allowed', 'balcony')
}

export interface NotificationPreferences {
  email: boolean
  push: boolean
  whatsapp: boolean
  frequency: NotificationFrequency
  quietHoursEnabled: boolean
  quietHoursStart: string  // "HH:MM" format, e.g. "22:00"
  quietHoursEnd: string    // "HH:MM" format, e.g. "08:00"
}

export interface UserPreferences {
  // Existing
  country: Country
  language: string
  theme: Theme
  savedSearches: SavedSearch[]
  savedListings: SavedListing[]

  // Appearance
  defaultViewMode: ViewMode
  cardDensity: CardDensity

  // Search defaults
  defaultCity: string
  defaultSort: SortOption
  defaultPriceMode: boolean  // true = total estimated, false = base rent
  listingFreshness: ListingFreshness
  dealbreakers: DealbreakersConfig

  // AI summary language
  aiSummaryLanguage: SummaryLanguage

  // Notifications
  notifications: NotificationPreferences

  // Privacy
  searchHistoryEnabled: boolean

  // Subscription
  subscription: UserSubscription
}

// Subscription Types

export type SubscriptionPlan = 'free' | 'penthouse'

export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | null

export interface UserSubscription {
  plan: SubscriptionPlan
  status: SubscriptionStatus
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  currentPeriodEnd: string | null  // ISO timestamp
  cancelAtPeriodEnd: boolean
  /** Billing source: 'apple' = App Store IAP (managed on the phone, no Stripe
   *  portal). Absent/null on legacy docs = Stripe. Written by search-api. */
  provider?: 'stripe' | 'apple' | null
}

export const DEFAULT_SUBSCRIPTION: UserSubscription = {
  plan: 'free',
  status: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
}

/** Features gated behind the Penthouse subscription. */
export type PremiumFeature =
  | 'extra_roostrs'
  | 'instant_notifications'
  | 'just_the_facts'
  | 'compare_mode'

// Board Sharing Types

export type BoardCollaboratorRole = 'write' | 'read'

export interface BoardCollaborator {
  uid: string
  email: string
  displayName: string
  photoURL: string
  role: BoardCollaboratorRole
  addedAt: string
}

export interface BoardSharing {
  isShared: boolean
  collaborators: BoardCollaborator[]
}

export interface SharedBoardAccess {
  ownerUid: string
  ownerName: string
  ownerEmail: string
  ownerPhotoURL: string
  role: BoardCollaboratorRole
  addedAt: string
}

// Planner Types

export type PlannerStatus = 'maybe' | 'visited' | 'applied'

export interface PlannerCard {
  listingId: string
  status: PlannerStatus
  rating: 1 | 2 | 3 | null  // 1-3 stars
  notes: string
  customTags: string[]  // e.g., "Has balcony", "Quiet neighbors"
  budgetMax: number | null  // User's max budget for comparison
  addedAt: string
  updatedAt: string
}

export interface ComparisonScore {
  listingId: string
  totalScore: number
  priceScore: number
  sizeScore: number
  areaScore: number
  reasoning: string
}

export interface ComparisonResult {
  scores: ComparisonScore[]
  winner: string  // listingId of the winner
  summary: string
}

// Lightweight per-listing preview for the header dropdown teaser — a subset
// of Listing fields returned in one batched request (see
// GET /notifications?include_preview=true), instead of one GET
// /listings/{id} per teaser row.
export interface NotificationPreviewListing {
  id: string
  title?: string
  price?: number
  price_currency?: string
  district?: string
  image_url?: string
}

// Notification Inbox Types
export interface NotificationItem {
  id: string
  user_id: string
  search_id?: string
  search_name: string
  listing_ids: string[]
  listing_count: number
  created_at: string
  read: boolean
  preview_image_url?: string
  preview_title?: string
  preview_listings: NotificationPreviewListing[]
  listings: Listing[]
}

export interface NotificationsResponse {
  notifications: NotificationItem[]
  total: number
  unread_count: number
}

export interface SummarizeResponse {
  summary: string
  language: string
}

// Analytics Types

export interface ComparableStats {
  count: number
  price_min: number
  price_max: number
  price_avg: number
  price_median: number
  area_avg: number
  price_per_sqm_avg: number
  median_age_days: number
}

export interface ListingAnalytics {
  listing_id: string
  price: number
  area?: number
  rooms?: number
  city?: string
  district?: string
  price_per_sqm?: number
  percentile: number
  listing_age_days: number
  comparable: ComparableStats
}

// Recent Search History (stored in Firestore + local Zustand)

export interface RecentSearch {
  id: string                          // unique id (crypto.randomUUID)
  type: 'text' | 'filters'           // which search mode was used
  query?: string                      // text query (for text searches)
  filters?: RentalFilters             // filter state (for filter searches)
  tags?: string[]                     // active feature tags
  city?: string                       // selected city
  districts?: string[]                // selected districts
  travelTime?: TravelTimeFilter | null
  sort?: SortOption
  displayLabel: string                // human-readable summary for the chip
  searchedAt: string                  // ISO timestamp
}
